import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, DownloadIcon, TrophyIcon, CalendarIcon, BookOpenIcon, CheckCircleIcon, LockIcon } from 'lucide-react';
import { Certificate } from '../hooks/useProgress';
interface CertificateDetailModalProps {
  certificate: Certificate | null;
  isOpen: boolean;
  onClose: () => void;
}
export function CertificateDetailModal({
  certificate,
  isOpen,
  onClose
}: CertificateDetailModalProps) {
  if (!certificate) return null;
  return <AnimatePresence data-id="element-552">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-553">
            {/* Modal Content */}
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 30
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 30
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden relative border-8 border-white" data-id="element-554">
              {/* Decorative Border Gradient */}
              <div className={`absolute inset-0 border-4 border-transparent rounded-xl pointer-events-none z-20`} style={{
            background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${certificate.isEarned ? '#21A366, #357BEB' : '#9ca3af, #d1d5db'}) border-box`
          }} data-id="element-555" />

              {/* Close Button */}
              <button onClick={onClose} className="absolute top-6 right-6 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-30" data-id="element-556">
                <XIcon className="w-5 h-5 text-gray-600" data-id="element-557" />
              </button>

              <div className="p-8 sm:p-12 text-center relative z-10" data-id="element-558">
                {/* Header Icon */}
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              delay: 0.2,
              duration: 0.8
            }} className={`
                    w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 shadow-xl
                    ${certificate.isEarned ? `bg-gradient-to-br ${certificate.gradient}` : 'bg-gray-200'}
                  `} data-id="element-559">
                  <TrophyIcon className={`w-10 h-10 ${certificate.isEarned ? 'text-white' : 'text-gray-400'}`} data-id="element-560" />
                </motion.div>

                <p className="text-gray-500 font-semibold uppercase tracking-widest text-sm mb-2" data-id="element-561">
                  Certificat de Absolvire
                </p>

                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 mb-6 leading-tight" data-id="element-562">
                  {certificate.courseName}
                </h2>

                {certificate.isEarned ? <div className="bg-green-50 border border-green-100 rounded-xl p-6 mb-8" data-id="element-563">
                    <p className="text-green-800 text-lg font-medium mb-2" data-id="element-564">
                      Felicitări! Ai absolvit acest curs cu succes.
                    </p>
                    <p className="text-green-600 text-sm" data-id="element-565">
                      Acest certificat atestă cunoștințele dobândite în cadrul
                      ecosistemului CautLocuință.ro.
                    </p>
                  </div> : <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 mb-8" data-id="element-566">
                    <p className="text-gray-800 text-lg font-medium mb-2" data-id="element-567">
                      Curs în desfășurare
                    </p>
                    <p className="text-gray-600 text-sm" data-id="element-568">
                      Finalizează toate modulele și lecțiile pentru a obține
                      certificatul.
                    </p>
                  </div>}

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-569">
                  <div className="p-4 rounded-xl bg-blue-50" data-id="element-570">
                    <BookOpenIcon className="w-6 h-6 text-blue-500 mx-auto mb-2" data-id="element-571" />
                    <p className="text-2xl font-bold text-gray-900" data-id="element-572">
                      {certificate.totalModules}
                    </p>
                    <p className="text-xs text-gray-500 uppercase font-bold" data-id="element-573">
                      Module
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-purple-50" data-id="element-574">
                    <CheckCircleIcon className="w-6 h-6 text-purple-500 mx-auto mb-2" data-id="element-575" />
                    <p className="text-2xl font-bold text-gray-900" data-id="element-576">
                      {certificate.totalLessons}
                    </p>
                    <p className="text-xs text-gray-500 uppercase font-bold" data-id="element-577">
                      Lecții
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-orange-50" data-id="element-578">
                    <CalendarIcon className="w-6 h-6 text-orange-500 mx-auto mb-2" data-id="element-579" />
                    <p className="text-sm font-bold text-gray-900 mt-1.5 mb-0.5" data-id="element-580">
                      {certificate.isEarned && certificate.earnedAt ? new Date(certificate.earnedAt).toLocaleDateString('ro-RO') : '-'}
                    </p>
                    <p className="text-xs text-gray-500 uppercase font-bold" data-id="element-581">
                      Dată
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-center space-x-4" data-id="element-582">
                  {certificate.isEarned ? <button className="flex items-center space-x-2 bg-[#357BEB] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#2868d8] transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-583">
                      <DownloadIcon className="w-5 h-5" data-id="element-584" />
                      <span data-id="element-585">Descarcă PDF</span>
                    </button> : <button disabled className="flex items-center space-x-2 bg-gray-200 text-gray-400 px-8 py-3 rounded-xl font-bold cursor-not-allowed" data-id="element-586">
                      <LockIcon className="w-5 h-5" data-id="element-587" />
                      <span data-id="element-588">Indisponibil</span>
                    </button>}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}